import { Waves } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { BathingSpot, SeaTemperature, UiLang } from "../lib/types";

interface Props {
  sea?: SeaTemperature;
  spots: BathingSpot[];
  lang: UiLang;
}

/**
 * Sjøtemperatur og badeplasser ved stoppet.
 *
 * To ting som ser ut som ett, og som er verdt å holde adskilt. Temperaturen er
 * ferskvare fra MET sin havmodell og finnes bare i badesesongen og bare der
 * modellen har dekning. Badeplassene er navngitte steder fra OSM, og de finnes
 * hele året — også ved vann i innlandet, der det ikke måles noen temperatur i
 * det hele tatt.
 *
 * Derfor står avstanden til målepunktet med. Null kilometer er stedet man står;
 * seks kilometer er fjorden utenfor, og det kan være to grader mellom dem. Et
 * tall uten den opplysningen ville lest som en måling der man skal bade.
 */

/** Fargeklassen for temperaturen. Grensene er de samme som `verdict` bruker. */
function band(celsius: number): string {
  if (celsius >= 18) return "sea-temp--warm";
  if (celsius >= 15) return "sea-temp--fine";
  if (celsius >= 12) return "sea-temp--brisk";
  return "sea-temp--cold";
}

export function SeaPanel({ sea, spots, lang }: Props) {
  const t = dictionaries[lang];

  // Ingen av delene: ingenting å vise. Kalleren skjuler seksjonen.
  if (!sea && spots.length === 0) return null;

  return (
    <div className="sea">
      {sea && (
        <div className="sea-now">
          <span className={`sea-temp ${band(sea.celsius)}`}>
            <Waves size={13} strokeWidth={2.2} />
            {/*
              Én desimal, ikke null og ikke to. Sjøen endrer seg langsomt nok at
              tideler betyr noe over en uke, og hundredeler er støy fra modellen.
            */}
            <b>{sea.celsius.toFixed(1)} °C</b>
          </span>
          <div className="sea-detail">
            <span className="sea-verdict">{sea.verdict}</span>
            <span className="sea-where">
              {sea.km < 0.5 ? t.seaHere : t.seaAt(sea.km)}
              {sea.waveHeightM !== undefined && ` · ${t.seaWaves(sea.waveHeightM)}`}
            </span>
          </div>
        </div>
      )}

      {spots.length > 0 && (
        <ul className="sea-spots">
          {spots.map((spot) => (
            <li key={spot.name}>
              <span className="sea-spot-name">{spot.name}</span>
              <span className="sea-spot-kind">{t.bathingKind[spot.kind]}</span>
              <span className="sea-spot-km">
                {spot.km} {t.unitKm}
              </span>
            </li>
          ))}
        </ul>
      )}

      {sea && <span className="sea-source">{t.seaSource}</span>}
    </div>
  );
}
